import { useState } from 'react'

import { Box, Button, Checkbox, FormControlLabel, TextField, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { Field, Form, Formik } from 'formik'
import * as Yup from 'yup'

import api from 'common/api'
import { CollegeOption } from 'components/CollegeAutocomplete'
import PhoneNumberInput from 'components/PhoneNumberInput'
import Title from 'components/Title'

import Confirmation from './Confirmation'

const useStyles = makeStyles((theme) => ({
  container: {
    textAlign: 'left',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  chipGroup: {
    display: 'flex',
    gap: theme.spacing(1),
  },
}))

const MAX_ADDITIONAL_INFO_LENGTH = 300
const validationSchema = Yup.object().shape({
  firstName: Yup.string().required('First name is required.'),
  lastName: Yup.string().required('Last name is required.'),
  title: Yup.string().required('Job title is required.'),
  phoneNumber: Yup.string().required('Phone number is required.'),
  additionalInfo: Yup.string().max(
    MAX_ADDITIONAL_INFO_LENGTH,
    `Additional information must be ${MAX_ADDITIONAL_INFO_LENGTH} characters or less.`,
  ),
  subscribe: Yup.boolean(),
})

type SubmitHubSpotTicketValues = {
  email: string
  selectedCollege: CollegeOption | null
  collegeName: string
  accessType: string
  firstName: string
  lastName: string
  title: string
  phoneNumber: string
  phoneExtension: string
  subscribe: boolean
  additionalInfo: string
}

type SubmitHubSpotTicketProps = {
  email: string
  selectedCollege: CollegeOption
  accessType: string
  validationError: string
}
const SubmitHubSpotTicket = ({ email, selectedCollege, accessType, validationError }: SubmitHubSpotTicketProps) => {
  const classes = useStyles()
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(false)

  const initialValues: SubmitHubSpotTicketValues = {
    email: email,
    selectedCollege: selectedCollege,
    accessType: accessType,
    // User input values
    firstName: '',
    lastName: '',
    collegeName: '', // This field is only required if the user selects "I don't see my school"
    title: '',
    phoneNumber: '',
    phoneExtension: '',
    subscribe: false,
    additionalInfo: '',
  }

  const onSubmit = async (values: SubmitHubSpotTicketValues) => {
    setError(false)

    const collegeName = values.selectedCollege?.scid === 0 ? values.collegeName : values.selectedCollege?.name
    const scid = values.selectedCollege?.scid ?? 0

    const payload = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      jobTitle: values.title,
      phoneNumber: values.phoneNumber,
      phoneExtension: values.phoneExtension,
      collegeName: collegeName ?? '',
      scid,
      accessType: values.accessType,
      additionalInfo: values.additionalInfo,
      subscribe: values.subscribe,
      validationError,
    }

    return api
      .post('/api/admissions/register/support', payload)
      .then(() => setSubmitted(true))
      .catch((err) => {
        console.error('Access request submission failed:', err)
        setError(true)
      })
  }

  if (submitted) {
    return <Confirmation supportTicketCreated={true} />
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      validateOnMount
      onSubmit={onSubmit}
    >
      {({ errors, touched, isValid, isSubmitting, values, setFieldValue }) => (
        <>
          <Title
            title="We'll help with your request"
            subtitle="We weren't able to automatically verify your institution. Fill out the form below and our team will review your request and follow up by email."
          />
          <Form>
            <div className={classes.container}>
              <Field
                as={TextField}
                variant="outlined"
                label="First Name"
                name="firstName"
                required
                error={touched.firstName && Boolean(errors.firstName)}
                helperText={touched.firstName && errors.firstName}
              />
              <Field
                as={TextField}
                variant="outlined"
                label="Last Name"
                name="lastName"
                required
                error={touched.lastName && Boolean(errors.lastName)}
                helperText={touched.lastName && errors.lastName}
              />
              {selectedCollege.scid === 0 && (
                <Field
                  as={TextField}
                  variant="outlined"
                  label="University/College Name"
                  name="collegeName"
                  required
                  error={touched.collegeName && Boolean(errors.collegeName)}
                  helperText={touched.collegeName && errors.collegeName}
                />
              )}
              <Field
                as={TextField}
                variant="outlined"
                label="Job Title"
                name="title"
                required
                error={touched.title && Boolean(errors.title)}
                helperText={touched.title && errors.title}
              />
              <Box
                display="flex"
                gridGap={8}
              >
                <PhoneNumberInput
                  name="phoneNumber"
                  label="Work Phone Number"
                  required
                />
                <Field
                  as={TextField}
                  variant="outlined"
                  label="Ext."
                  name="phoneExtension"
                />
              </Box>
              <Field
                as={TextField}
                multiline
                rows={4}
                variant="outlined"
                label="Additional Information (Optional)"
                name="additionalInfo"
                error={values.additionalInfo?.length > MAX_ADDITIONAL_INFO_LENGTH}
                helperText={`${MAX_ADDITIONAL_INFO_LENGTH - values.additionalInfo?.length} characters left`}
              />
              <FormControlLabel
                label="I'd like to receive updates about Scoir's products and services. "
                control={
                  <Field
                    id="subscribe"
                    name="subscribe"
                    as={Checkbox}
                    disableRipple={false}
                    color="primary"
                    checked={values.subscribe}
                    onChange={() => setFieldValue('subscribe', !values.subscribe, true)}
                    onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
                      if (event.key === 'Enter') {
                        setFieldValue('subscribe', !values.subscribe, true)
                      }
                    }}
                  />
                }
              />
              {error && (
                <Typography
                  color="error"
                  variant="body2"
                >
                  Something went wrong submitting your request. Please try again.
                </Typography>
              )}
              <Button
                type="submit"
                color="primary"
                variant="contained"
                disabled={!isValid || isSubmitting}
              >
                Submit My Request
              </Button>
            </div>
          </Form>
        </>
      )}
    </Formik>
  )
}

export default SubmitHubSpotTicket
